import { Icon } from "../ui/icons.js";

/** The bits every tab beside Studio shares: one heading, one row of filters, one honest empty state. */

/**
 * The type scale a tab leads with, settled on Analytics on 2026-09-20 and shared so two screens cannot drift apart:
 * a small kicker naming what this is, the strongest true sentence in display type, and one line under it.
 *
 * Coral belongs on the kicker only when the sentence under it is the one thing to act on. The colour is the product
 * saying *this one*, and it says it once a screen.
 */
export const KICKER = "text-[11px] font-semibold uppercase tracking-[.06em]";
export const HEADLINE = "font-display text-[24px] md:text-[28px] leading-[1.15] font-bold tracking-[-.02em] mt-1.5 text-balance";
export const UNDER = "text-[13px] text-muted mt-2 leading-relaxed max-w-[52ch]";
/**
 * One row, not three. The filters sit beside the heading rather than under it, and the heading is a line rather
 * than a banner: this strip is the same on every tab and it is the last thing anybody came to read, so every pixel
 * it takes is one the numbers do not get (2026-09-20, the owner, looking at Analytics on the box).
 */
export function TabBar({ title, note, nav, children }:
  { title: string; note?: string; nav?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="px-4 md:px-6 py-2.5 border-b border-line flex flex-wrap items-center gap-x-3 gap-y-2">
      <h1 className="text-[15px] font-semibold">{title}</h1>
      {note ? <span className="text-[13px] text-muted">{note}</span> : null}
      {/* Which view you are looking at belongs beside the tab's own name; what you may *do* belongs at the far
          right. Putting both in one row on the right made a press that spends money look like a fourth view
          (2026-09-21, the owner, looking at SEO on the box). */}
      {nav ? <div className="flex items-center">{nav}</div> : null}
      {children ? <div className="flex flex-wrap items-center gap-1.5 ml-auto">{children}</div> : null}
    </div>
  );
}

/**
 * The screens a tab has, as a menu down the left: a label, one line under it saying what is there, and the one you
 * are on raised out of the column. Along the top as a row of pills on a phone.
 *
 * Settled on Business (03 §business context) and taken to every tab with more than one screen on 2026-09-26, at the
 * owner's word: a segment beside the tab's name had room for a word per screen and nothing else, and a menu says
 * what each screen is *for* before it is pressed. `mark` is what sits before the label — a tick on a checklist, the
 * screen's icon otherwise. `head` is the tab's own name at the top, `foot` what belongs at the bottom on a desk, and
 * `action` the one press the tab carries: at the bottom on a desk, at the end of the row on a phone, never among the
 * screens, so a press that spends money never reads as one more of them.
 */
export type SideItem<K extends string> = { key: K; label: string; hint: string; mark?: React.ReactNode; count?: number; dim?: boolean };

export function SideMenu<K extends string>({ label, items, current, onPick, narrow, head, foot, action }: {
  label: string; items: ReadonlyArray<SideItem<K>>; current: K; onPick: (k: K) => void; narrow: boolean;
  head?: React.ReactNode; foot?: React.ReactNode; action?: React.ReactNode;
}) {
  const count = (i: SideItem<K>) => (i.count ? <span className="text-coral tabular-nums">{i.count}</span> : null);
  if (narrow) {
    return (
      <nav aria-label={label} className="shrink-0 flex items-center gap-1.5 overflow-x-auto px-3 py-2 bg-panel border-b border-line">
        {items.map((i) => (
          <button key={i.key} onClick={() => onPick(i.key)} aria-current={i.key === current ? "page" : undefined}
            className={`flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 rounded-full text-xs font-semibold ${i.key === current ? "bg-ink text-white" : "bg-page text-muted"}`}>
            {i.mark}{i.label}{count(i)}
          </button>
        ))}
        {action ? <div className="ml-auto shrink-0 pl-1.5">{action}</div> : null}
      </nav>
    );
  }
  return (
    <nav aria-label={label} className="w-[260px] shrink-0 bg-page border-r border-line px-3 py-4 flex flex-col gap-0.5 overflow-y-auto">
      {head ? <div className="flex items-center gap-2.5 px-1.5 pb-3">{head}</div> : null}
      {items.map((i) => (
        <button key={i.key} onClick={() => onPick(i.key)} aria-current={i.key === current ? "page" : undefined}
          className={`text-left flex items-center gap-2.5 px-2.5 py-2 rounded-[9px] border ${i.key === current ? "bg-panel border-line shadow-sm" : "border-transparent hover:bg-panel/60"} ${i.dim ? "opacity-60" : ""}`}>
          {i.mark}
          <span className="min-w-0 flex-1 flex flex-col">
            <span className={`text-[13px] flex items-center gap-1.5 ${i.key === current ? "font-bold" : "font-medium"}`}>{i.label}{count(i)}</span>
            <span className="text-[11px] text-muted truncate">{i.hint}</span>
          </span>
        </button>
      ))}
      {foot || action ? <div className="mt-auto pt-4 px-1.5 flex flex-col gap-1.5">{action}{foot}</div> : null}
    </nav>
  );
}

/** The head of a menu for a tab that is not about one business: the tab's icon on an ink tile, its name, one line. */
export function SideHead({ icon, title, note }: { icon: string; title: string; note?: string }) {
  return (
    <>
      <span className="w-10 h-10 rounded-[10px] grid place-items-center bg-ink text-white shrink-0"><Icon name={icon} size={18} /></span>
      <span className="min-w-0 flex flex-col">
        <h1 className="text-[14px] font-extrabold truncate">{title}</h1>
        {note ? <span className="text-[11.5px] text-muted truncate">{note}</span> : null}
      </span>
    </>
  );
}

/** The mark before a label on a menu that is not a checklist: the screen's icon, in the slot a tick would take. */
export const sideIcon = (name: string) => <span className="w-[18px] h-[18px] grid place-items-center shrink-0 opacity-60"><Icon name={name} size={15} /></span>;

export function Tab({ on, onClick, title, children }: { on: boolean; onClick: () => void; title?: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${on ? "bg-ink text-white" : "bg-page text-ink-2 hover:bg-line"}`}
    >
      {children}
    </button>
  );
}

/**
 * A titled card: one question, one answer, one thing you may do about it.
 *
 * It is here rather than in a tab because a screen built out of these reads as a list of answers, and a screen
 * built out of headings and paragraphs reads as a document somebody has to sort out for themselves. The heading is
 * the question in the person's words, `note` is what it is about, and `action` is the one press it carries.
 */
export function Card({ title, note, action, children }:
  { title?: string; note?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-panel p-4">
      {title || action ? (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
          {title ? <h2 className="text-[13.5px] font-semibold">{title}</h2> : null}
          {note ? <span className="text-[11.5px] text-muted">{note}</span> : null}
          {action ? <div className="ml-auto flex items-center gap-2">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/**
 * What a designed-but-unbuilt tab says.
 *
 * It matters that this is not an empty state. An empty month grid means a business with nothing scheduled; an
 * empty table means a post nobody watched. Both are things a customer could believe, and neither is true here.
 */
export function NotBuilt({ what, note }: { what: string; note?: string | undefined }) {
  return (
    <div className="mb-5 rounded-[10px] border border-dashed border-line-2 bg-panel px-4 py-3">
      <p className="text-[13px] font-semibold">Nothing is stored for the {what} yet.</p>
      <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed">
        {note ?? "This is a design, not a feature."} What is below is what it will hold, so it can be argued with
        before it is built.
      </p>
    </div>
  );
}
